import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../services/supabaseClient"; // Certifique-se de que este caminho está correto
import { authService } from "../services/auth"; // Importar authService

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Estado de carregamento inicial

  useEffect(() => {
    const checkInitialAuth = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("Erro ao obter sessão do Supabase:", error);
          setIsAuthenticated(false);
          setUser(null);
        } else if (session) {
          // Se houver uma sessão, tente obter os detalhes completos do usuário
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error("Erro na verificação inicial de autenticação:", error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false); // Finaliza o carregamento independentemente do resultado
      }
    };

    checkInitialAuth();

    // Listener para mudanças no estado de autenticação do Supabase
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_IN") {
          authService.getCurrentUser().then((currentUser) => {
            setUser(currentUser);
            setIsAuthenticated(true);
          });
        } else if (event === "SIGNED_OUT") {
          setUser(null);
          setIsAuthenticated(false);
        }
        // Para outros eventos como 'USER_UPDATED', 'PASSWORD_RECOVERY', etc.,
        // você pode adicionar lógica específica se necessário.
      }
    );

    return () => {
      if (authListener && authListener.unsubscribe) {
        authListener.unsubscribe();
      }
    };
  }, []);

  const login = async (email, password, rememberMe) => {
    setIsLoading(true);
    try {
      const data = await authService.login(email, password, rememberMe);
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      setIsAuthenticated(true);
      return data;
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    // Você pode adicionar outras funções do authService aqui se precisar expô-las
    // Ex: signUp, sendPasswordResetEmail, getCurrentUserRole
    getCurrentUserRole: authService.getCurrentUserRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
